import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import { renderPageBlock } from 'src/common/components/page-blocks/renderPageBlock';
import { usePublicPageEdit } from 'src/common/components/PublicPageEditContext';
import { LanguageKeys } from 'src/common/constants/languages';
import {
  getColumnCssOrder,
  getColumnSpanForBreakpoint,
  normalizeColumnsLayout,
  orphanCenteredContainerSx,
} from 'src/helpers/columnsLayoutUtils';
import { columnFlexSx } from 'src/helpers/heroLayoutUtils';
import { nestedPageBlockWrapperSx } from 'src/helpers/blockSpacing';
import { evaluateVisibilityFilter, VisibilityContext } from 'src/helpers/visibilityFilter';
import { PlaceholderRenderContext } from 'src/helpers/placeholderRegistry';
import { isSocialShareOgPreview } from 'src/helpers/socialShareOgLayout';
import { shouldRenderTextBlockInline } from 'src/helpers/signupPageLayoutUtils';
import { IColumnsLayout } from 'src/interfaces/PageColumn';
import { IEdition } from 'src/interfaces/Edition';
import { ITextBlock } from 'src/interfaces/PageBlock';

interface ColumnsLayoutRendererProps {
  layout: IColumnsLayout;
  lang: LanguageKeys;
  visibilityContext: VisibilityContext;
  placeholderContext: PlaceholderRenderContext;
  currentEdition?: IEdition;
  inHero?: boolean;
  applyVisibilityRules?: boolean;
  contentKey?: string;
}

export default function ColumnsLayoutRenderer({
  layout: rawLayout,
  lang,
  visibilityContext,
  placeholderContext,
  currentEdition,
  inHero,
  applyVisibilityRules = true,
  contentKey = 'signup',
}: ColumnsLayoutRendererProps) {
  const edit = usePublicPageEdit();
  const isEditMode = edit?.isEditMode ?? false;
  const layout = normalizeColumnsLayout(rawLayout) ?? rawLayout;
  const centeredOrphanSx = orphanCenteredContainerSx(layout);
  const ogFixedLayout = isSocialShareOgPreview(contentKey);
  const columnCount = layout.columns.length;
  const ogColumnSpan = columnCount > 0 ? Math.floor(12 / columnCount) : 12;

  return (
    <Grid
      container
      spacing={{ xs: 2, sm: 3 }}
      sx={{
        flexWrap: 'wrap',
        flexDirection: ogFixedLayout ? 'row' : { xs: 'column', sm: 'row' },
        alignItems: 'stretch',
        ...(ogFixedLayout ? {} : centeredOrphanSx),
        mb: 2,
        width: '100%',
      }}
    >
      {layout.columns.map((col, originalIndex) => {
        const cssOrder = getColumnCssOrder(layout, originalIndex);

        return (
          <Grid
            key={`col-${originalIndex}`}
            data-column-index={originalIndex}
            data-testid={`columns-layout-col-${originalIndex}`}
            size={
              ogFixedLayout
                ? ogColumnSpan
                : {
                    xs: getColumnSpanForBreakpoint(layout, originalIndex, 'xs'),
                    sm: getColumnSpanForBreakpoint(layout, originalIndex, 'sm'),
                    md: getColumnSpanForBreakpoint(layout, originalIndex, 'md'),
                  }
            }
            sx={{
              display: 'flex',
              flexDirection: 'column',
              order: cssOrder,
              alignSelf: 'stretch',
            }}
          >
            <Box sx={columnFlexSx(col.alignH, col.alignV)}>
              {col.blocks.map(block => {
                if (
                  block.type === 'text' &&
                  !shouldRenderTextBlockInline(block as ITextBlock, isEditMode)
                ) {
                  return null;
                }
                if (
                  applyVisibilityRules &&
                  !isEditMode &&
                  block.type !== 'results' &&
                  !evaluateVisibilityFilter(block.visibility, visibilityContext)
                ) {
                  return null;
                }
                const node = renderPageBlock(block, {
                  lang,
                  placeholderContext,
                  currentEdition,
                  inHero,
                  visibilityContext,
                  applyVisibilityRules,
                  contentKey,
                });
                if (!node) return null;
                return (
                  <Box key={block.id} sx={nestedPageBlockWrapperSx(block)}>
                    {node}
                  </Box>
                );
              })}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}
